// src/core/metrics/mfh.ts
// Kennzahlen, Score und Entscheidungs-Label des MFH-Analyzers.
// Aus src/routes/MFHCheck.tsx verschoben, nicht verändert: gleiche Formeln,
// gleiche Reihenfolge der Operationen. Keine React-Abhängigkeit.

import { BEWIRTSCHAFTUNG_PAUSCHALE_PCT } from "../score/dealScoreConfig.js";

export type DecisionLabel = "RENTABEL" | "GRENZWERTIG" | "NICHT_RENTABEL";

export type MfhMetricsInput = {
  kaufpreis: number;
  priceAdjPct: number;
  applyAdjustments: boolean;
  // Kaufnebenkosten gesamt als Anteil (0..1)
  nkPct: number;
  nkRenovierung: number;
  nkSanierung: number;
  // Miete (Summe aus Flächenmanagement) und Anpassungen
  grossRentYear: number;
  rentAdjPct: number;
  leerstandPct: number;
  nichtUmlagefaehigeKosten: number;
  capexRuecklagePctBrutto: number;
  // Bewirtschaftung Pauschal/Einzeln (Korrektur der frueheren "3 % uebernehmen"-Vorgabe,
  // Baustein 5): optional, damit der unveraenderte "freier Einstieg"-Pfad (MFHCheck.tsx,
  // !hasDealId) ohne Modus weiterhin exakt wie bisher rechnet (Legacy-Zweig in
  // computeMfhMetrics -- nichtUmlagefaehigeKosten + capexRuecklagePctBrutto*grossRentAdj,
  // unveraendert). Nur das "Annahmen pruefen"-Formular setzt `bewirtschaftungModus`.
  // Pauschal: effRentYear * BEWIRTSCHAFTUNG_PAUSCHALE_PCT.mfh (ersetzt beide Legacy-Felder,
  // score-identische Formel zum /check-Schnellcheck, siehe quickCheckGenericAdapter.ts).
  // Einzeln: nichtUmlagefaehigeKosten (wie Legacy) + verwaltung + instandhaltungJeM2Jahr*flaeche.
  bewirtschaftungModus?: "pauschal" | "einzeln";
  verwaltung?: number;
  instandhaltungJeM2Jahr?: number;
  flaeche?: number;
  // Stress-Pass-Fix (18.10.2026, Dominiks Vorgabe): Pauschal wird auf der BASIS-Kaltmiete
  // (vor Mietstress) ermittelt und bleibt unter Mietstress fix -- wie Einzeln. Der Aufrufer
  // setzt dieses Feld nur fuer den Stress-Durchlauf (auf effRentYear des Basis-Durchlaufs);
  // bleibt es undefined (Basis-Durchlauf selbst, oder jeder alte Aufrufer), faellt die
  // Pauschal-Formel auf das eigene effRentYear zurueck -- identisches Ergebnis wie bisher.
  pauschaleBasisMiete?: number;
  // Finanzierung
  eigenkapital: number;
  manualLoan: boolean;
  darlehenManual: number;
  zins: number;
  tilgung: number;
};

export type MfhMetrics = {
  kaufpreisView: number;
  nkSumPercent: number;
  nkSum: number;
  allIn: number;
  grossRentAdj: number;
  effRentYear: number;
  capexRuecklage: number;
  /** Nur im Einzeln-Modus > 0 (Pauschal/Legacy: 0) -- fuer die Breakdown-Anzeige. */
  verwaltungEff: number;
  noi: number;
  loan: number;
  finanzierungsluecke: number;
  ekBedarf: number;
  annuitaetJahr: number;
  annuitaetMonat: number;
  zinsMonat: number;
  tilgungMonat: number;
  noiYield: number;
  dscr: number | null;
  dscrScorePart: number;
  score: number;
  scorePct: number;
  monthlyEffRent: number;
  monthlyOpex: number;
  monthlyCapex: number;
  /** Nur im Einzeln-Modus > 0 -- fuer die Breakdown-Anzeige (Baustein 5). */
  monthlyVerwaltung: number;
  monthlyCF: number;
  decisionLabel: DecisionLabel;
};

function clamp01(x: number) {
  return Math.max(0, Math.min(1, x));
}

function scale(x: number, a: number, b: number) {
  if (b === a) return 0;
  return clamp01((x - a) / (b - a));
}

export function computeMfhMetrics(input: MfhMetricsInput): MfhMetrics {
  const {
    kaufpreis, priceAdjPct, applyAdjustments, nkPct, nkRenovierung, nkSanierung,
    grossRentYear, rentAdjPct, leerstandPct, nichtUmlagefaehigeKosten, capexRuecklagePctBrutto,
    eigenkapital, manualLoan, darlehenManual, zins, tilgung,
  } = input;

  // Preis-Anpassung + All-in
  const kaufpreisAdj = Math.round(kaufpreis * (1 + priceAdjPct));
  const kaufpreisView = applyAdjustments ? kaufpreisAdj : kaufpreis;
  const nkSumPercent = Math.round(kaufpreisView * nkPct);
  const nkSum = nkSumPercent + Math.max(0, nkRenovierung) + Math.max(0, nkSanierung);
  const allIn = kaufpreisView + nkSum;

  // Miete-Anpassung + Leerstand
  const grossRentAdj = grossRentYear * (1 + (applyAdjustments ? rentAdjPct : 0));
  const effRentYear = grossRentAdj * (1 - clamp01(leerstandPct));

  // Bewirtschaftung Pauschal/Einzeln (Baustein 5) -- siehe Feldkommentar in MfhMetricsInput.
  let nichtUmlagefaehigEff: number;
  let verwaltungEff: number;
  let capexRuecklage: number;
  if (input.bewirtschaftungModus === "pauschal") {
    nichtUmlagefaehigEff = 0;
    verwaltungEff = 0;
    capexRuecklage = (input.pauschaleBasisMiete ?? effRentYear) * BEWIRTSCHAFTUNG_PAUSCHALE_PCT.mfh;
  } else if (input.bewirtschaftungModus === "einzeln") {
    nichtUmlagefaehigEff = nichtUmlagefaehigeKosten;
    verwaltungEff = Math.max(0, input.verwaltung ?? 0);
    capexRuecklage = Math.max(0, input.instandhaltungJeM2Jahr ?? 0) * Math.max(0, input.flaeche ?? 0);
  } else {
    // Legacy (kein Modus, z.B. der unveraenderte freie-Einstieg-Pfad): exakt wie bisher.
    nichtUmlagefaehigEff = nichtUmlagefaehigeKosten;
    verwaltungEff = 0;
    capexRuecklage = grossRentAdj * capexRuecklagePctBrutto;
  }
  const noi = Math.max(0, effRentYear - nichtUmlagefaehigEff - verwaltungEff - capexRuecklage);

  // Finanzierung: Das EK deckt zuerst die Nebenkosten, der Kredit den Rest des Kaufpreises
  // (Kredit = KP − (EK − NK)). Reicht das EK nicht für die Nebenkosten, bleibt eine Lücke,
  // statt stillschweigend einen zu großen Kredit anzunehmen. Manuelles Darlehen: wie eingegeben.
  const ekEingabe = Math.max(0, eigenkapital);
  const ekFuerNk = Math.min(ekEingabe, nkSum);
  const ekRest = ekEingabe - ekFuerNk;
  const loanAuto = Math.max(0, kaufpreisView - ekRest);
  const loan = manualLoan ? Math.max(0, darlehenManual) : loanAuto;
  // Lücke: Bedarf (All-in) minus EK minus Kredit; 0, wenn die Quellen reichen
  const finanzierungsluecke = Math.max(0, allIn - ekEingabe - loan);
  // EK-Bedarf: All-in minus Kredit (inkl. Nebenkosten)
  const ekBedarf = allIn - loan;
  const annuitaetJahr = loan * (zins + tilgung);
  const annuitaetMonat = annuitaetJahr / 12;
  const zinsMonat = (loan * zins) / 12;
  const tilgungMonat = (loan * tilgung) / 12;

  // KPIs & Score
  const noiYield = kaufpreisView > 0 ? noi / kaufpreisView : 0;
  // Ohne Darlehen (annuitaetJahr<=0, z.B. Barkauf) gibt es kein Ausfallrisiko beim
  // Schuldendienst -- dscr ist dann "nicht anwendbar" (null), nicht 0.
  const dscr = annuitaetJahr > 0 ? noi / annuitaetJahr : null;
  const dscrScorePart = dscr == null ? 0.6 : scale(dscr, 1.1, 1.6);
  const score = clamp01(
    scale(noiYield, 0.035, 0.07) * 0.55 + dscrScorePart * 0.45
  );
  const scorePct = Math.round(score * 100);

  // Monatsrechnung (Y1)
  const monthlyEffRent = effRentYear / 12;
  const monthlyOpex = nichtUmlagefaehigEff / 12;
  const monthlyCapex = capexRuecklage / 12;
  const monthlyVerwaltung = verwaltungEff / 12;
  const monthlyCF = monthlyEffRent - monthlyOpex - monthlyVerwaltung - monthlyCapex - annuitaetMonat;

  // Entscheidung / Ampel
  let decisionLabel: DecisionLabel;
  if (monthlyCF >= 100 && (dscr === null || dscr >= 1.2) && noiYield >= 0.05) {
    decisionLabel = "RENTABEL";
  } else if (monthlyCF >= 0) {
    decisionLabel = "GRENZWERTIG";
  } else {
    decisionLabel = "NICHT_RENTABEL";
  }

  return {
    kaufpreisView, nkSumPercent, nkSum, allIn, grossRentAdj, effRentYear, capexRuecklage, verwaltungEff, noi,
    loan, finanzierungsluecke, ekBedarf, annuitaetJahr, annuitaetMonat, zinsMonat, tilgungMonat, noiYield, dscr, dscrScorePart,
    score, scorePct, monthlyEffRent, monthlyOpex, monthlyCapex, monthlyVerwaltung, monthlyCF, decisionLabel,
  };
}

/** Break-even-Kaufpreis (Dominiks Vorgabe, Baustein 4, Bankbericht aus der Akte): aus
 *  MFHCheck.tsx extrahiert (`breakEvenPriceForCashflowZero`), UNVERAENDERT uebernommen --
 *  Feld-fuer-Feld-Paritaet zum heutigen PDF erfordert exakt dieselbe Bisektion, nicht den
 *  generischen Akte-Solver (dealScoreBreakeven.ts), der mit anderer Iterationstiefe/
 *  Klammerung leicht abweichende Werte liefern wuerde. */
function clamp01Local(x: number) {
  return Math.max(0, Math.min(1, x));
}

/** Gemeinsame Bewirtschaftungskosten-Formel fuer beide Break-even-Helfer unten (Baustein 5) --
 *  spiegelt EXAKT den Modus-Zweig aus computeMfhMetrics() oben, damit ein Pauschal- oder
 *  Einzeln-Deal im Bankbericht denselben Break-even-Preis/Mindestmiete bekommt wie auf der
 *  Objektseite. `grossAdj`/`eff` sind die bereits angepassten (Spielwiese) Jahreswerte. */
function bewirtschaftungskostenForBreakEven(
  base: {
    nichtUmlagefaehigeKosten: number;
    capexPctBrutto: number;
    bewirtschaftungModus?: "pauschal" | "einzeln";
    verwaltung?: number;
    instandhaltungJeM2Jahr?: number;
    flaeche?: number;
  },
  grossAdj: number,
  eff: number
): number {
  if (base.bewirtschaftungModus === "pauschal") {
    return eff * BEWIRTSCHAFTUNG_PAUSCHALE_PCT.mfh;
  }
  if (base.bewirtschaftungModus === "einzeln") {
    return (
      base.nichtUmlagefaehigeKosten +
      Math.max(0, base.verwaltung ?? 0) +
      Math.max(0, base.instandhaltungJeM2Jahr ?? 0) * Math.max(0, base.flaeche ?? 0)
    );
  }
  return base.nichtUmlagefaehigeKosten + grossAdj * base.capexPctBrutto;
}

export function breakEvenPriceForCashflowZero(base: {
  basePrice: number;
  area: number;
  grossRentY: number;
  rentAdjPct: number;
  leerstandPct: number;
  nichtUmlagefaehigeKosten: number;
  capexPctBrutto: number;
  ek: number;
  zins: number;
  tilgung: number;
  manualLoan: boolean;
  bewirtschaftungModus?: "pauschal" | "einzeln";
  verwaltung?: number;
  instandhaltungJeM2Jahr?: number;
  flaeche?: number;
}): number | null {
  if (base.manualLoan || base.area <= 0) return null;
  const target = 0;
  let lo = Math.max(1, base.basePrice * 0.5), hi = base.basePrice * 1.5;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    const grossAdj = base.grossRentY * (1 + base.rentAdjPct);
    const eff = grossAdj * (1 - clamp01Local(base.leerstandPct));
    const bewirtschaftung = bewirtschaftungskostenForBreakEven(base, grossAdj, eff);
    const noi = Math.max(0, eff - bewirtschaftung);
    const loan = Math.max(0, mid - Math.max(0, base.ek));
    const ann = loan * (base.zins + base.tilgung);
    const cf = noi - ann;
    if (cf > target) lo = mid;
    else hi = mid;
  }
  return Math.round((lo + hi) / 2);
}

/** Aus MFHCheck.tsx extrahiert (`breakEvenRentPerM2ForCashflowZero`), unveraendert
 *  uebernommen (siehe Kommentar oben). */
export function breakEvenRentPerM2ForCashflowZero(base: {
  price: number;
  area: number;
  rentPerM2Now: number;
  leerstandPct: number;
  nichtUmlagefaehigeKosten: number;
  capexPctBrutto: number;
  ek: number;
  zins: number;
  tilgung: number;
  manualLoan: boolean;
  loan: number;
  bewirtschaftungModus?: "pauschal" | "einzeln";
  verwaltung?: number;
  instandhaltungJeM2Jahr?: number;
  flaeche?: number;
}): number | null {
  if (base.area <= 0) return null;
  const target = 0;
  let lo = 0, hi = Math.max(20, base.rentPerM2Now * 2.5);
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    const gross = base.area * mid * 12;
    const eff = gross * (1 - clamp01Local(base.leerstandPct));
    const bewirtschaftung = bewirtschaftungskostenForBreakEven(base, gross, eff);
    const noi = Math.max(0, eff - bewirtschaftung);
    const L = base.manualLoan ? base.loan : Math.max(0, base.price - Math.max(0, base.ek));
    const ann = L * (base.zins + base.tilgung);
    const cf = noi - ann;
    if (cf > target) hi = mid;
    else lo = mid;
  }
  return Number(((lo + hi) / 2).toFixed(2));
}
